import { Card, Typography, Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, IconButton, Switch } from "@mui/material";
import { useState } from "react";
import { post, put } from "src/utils/networkUtils";
import { withTenant } from "src/utils/withTenant";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import EditIcon from '@mui/icons-material/Edit';
import MatchDetailsDialog from "./MatchDetailsDialog";
import MatchLiveScoringDialog from "./MatchLiveScoringDialog";

export default function FixtureCard({ fixture, onScoringComplete, tenantId }) {
    const [scoring, setScoring] = useState(false);
    const [showMatch, setShowMatch] = useState(null);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(fixture.status);
    const [updatingScoringEnabled, setUpdatingScoringEnabled] = useState(false);
    const [liveScoringDialogOpen, setLiveScoringDialogOpen] = useState(false);
    const [scoringConfirmDialogOpen, setScoringConfirmDialogOpen] = useState(false);

    const getFeedStatusColor = (status) => {
        switch (status) {
            case 'ok':
                return 'green';
            case 'empty':
                return 'orange';
            case 'failed':
                return 'red';
            default:
                return 'default';
        }
    }

    const handleRunScoring = async () => {
        setScoring(true);
        setScoringConfirmDialogOpen(false);
        try {
            await post(withTenant(`/admin/fixtures/${fixture.fixtureId}/score`, tenantId));
            if (onScoringComplete) {
                onScoringComplete();
            }
        } catch (error) {
            console.error('Failed to run scoring:', error);
        } finally {
            setScoring(false);
        }
    };

    const handleRunScoringClick = () => {
        setScoringConfirmDialogOpen(true);
    };

    const handleStatusChange = async () => {
        try {
            await put(withTenant(`/admin/fixtures/${fixture.fixtureId}`, tenantId), { status: selectedStatus });
            setStatusDialogOpen(false);
            if (onScoringComplete) {
                onScoringComplete();
            }
        } catch (error) {
            console.error('Failed to update fixture status:', error);
        }
    };

    const handleScoringEnabledToggle = async (event) => {
        const newScoringEnabled = event.target.checked;
        setUpdatingScoringEnabled(true);
        try {
            await put(withTenant(`/admin/fixtures/${fixture.fixtureId}`, tenantId), { isScoringEnabled: newScoringEnabled });
            if (onScoringComplete) {
                onScoringComplete();
            }
        } catch (error) {
            console.error('Failed to update scoring enabled status:', error);
        } finally {
            setUpdatingScoringEnabled(false);
        }
    };

    return (
        <Card
            sx={{
                p: 2,
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                },
                borderRadius: 2,
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Header: Date/Time, Status Badge, and Scored Indicator */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: 'block',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                            }}
                        >
                            {new Date(fixture.startDateUtc).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => setStatusDialogOpen(true)}
                            sx={{ p: 0.25, ml: 0.5 }}
                        >
                            <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                            sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: fixture.status === 'LIVE' ? 'error.lighter' :
                                    fixture.status === 'FINISHED' ? 'success.lighter' : 'info.lighter',
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.65rem',
                                    color: fixture.status === 'LIVE' ? 'error.main' :
                                        fixture.status === 'FINISHED' ? 'success.main' : 'info.main',
                                }}
                            >
                                {fixture.status}
                            </Typography>
                        </Box>
                        {fixture.isScored ? (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        ) : (
                            <PendingIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                        )}
                    </Box>
                </Box>

                {/* Score Display */}
                <Box sx={{
                    bgcolor: 'background.default',
                    borderRadius: 2,
                    p: 1.5,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                lineHeight: 1,
                            }}
                        >
                            {fixture.details.HomeTeamName}
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                color: 'primary.main',
                                lineHeight: 1,
                            }}
                        >
                            {fixture.details.HomeTeamScore}
                        </Typography>
                    </Box>

                    <Box sx={{
                        borderTop: 1,
                        borderColor: 'divider',
                        my: 0.75
                    }} />

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                lineHeight: 1,
                            }}
                        >
                            {fixture.details.AwayTeamName}
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                color: 'primary.main',
                                lineHeight: 1,
                            }}
                        >
                            {fixture.details.AwayTeamScore}
                        </Typography>
                    </Box>
                </Box>

                {/* Info Row: Lineups and Feed Status */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    px: 0.5,
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                            }}
                        >
                            LINEUPS
                        </Typography>
                        <Box
                            sx={{
                                bgcolor: 'primary.lighter',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1.5,
                                minWidth: 28,
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    color: 'primary.main',
                                    fontSize: '0.75rem',
                                }}
                            >
                                {fixture.lineupsInScoringCount || 0}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                            }}
                        >
                            FEED
                        </Typography>
                        <Box
                            sx={{
                                px: 1.25,
                                py: 0.35,
                                borderRadius: 1.5,
                                bgcolor: getFeedStatusColor(fixture.feedStatus) === 'green' ? 'success.lighter' :
                                    getFeedStatusColor(fixture.feedStatus) === 'orange' ? 'warning.lighter' :
                                        getFeedStatusColor(fixture.feedStatus) === 'red' ? 'error.lighter' : 'grey.200',
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    color: getFeedStatusColor(fixture.feedStatus) === 'green' ? 'success.main' :
                                        getFeedStatusColor(fixture.feedStatus) === 'orange' ? 'warning.main' :
                                            getFeedStatusColor(fixture.feedStatus) === 'red' ? 'error.main' : 'text.secondary',
                                    fontSize: '0.7rem',
                                }}
                            >
                                {fixture.feedStatus ? fixture.feedStatus.toUpperCase() : 'N/A'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Divider */}
                <Box sx={{ borderTop: 1, borderColor: 'divider' }} />

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="medium"
                            onClick={handleRunScoringClick}
                            disabled={scoring}
                            startIcon={scoring ? <CircularProgress size={16} color="inherit" /> : null}
                            fullWidth
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                py: 1,
                                '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: 2,
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            {scoring ? 'Scoring...' : 'Run Scoring'}
                        </Button>
                        <Button
                            variant="outlined"
                            size="medium"
                            onClick={() => setShowMatch(fixture)}
                            fullWidth
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                py: 1,
                                '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: 2,
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            Details
                        </Button>
                    </Box>

                    <Button
                        variant="contained"
                        size="medium"
                        onClick={() => setLiveScoringDialogOpen(true)}
                        fullWidth
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            py: 1,
                            boxShadow: 2,
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 4,
                            },
                            transition: 'all 0.2s ease',
                        }}
                    >
                        Score Manually
                    </Button>
                </Box>

                {/* Divider */}
                <Box sx={{ borderTop: 1, borderColor: 'divider' }} />

                {/* Enable Scoring Toggle */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 0.5,
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Scoring Enabled
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {updatingScoringEnabled && <CircularProgress size={16} />}
                        <Switch
                            checked={fixture.isScoringEnabled !== false}
                            onChange={handleScoringEnabledToggle}
                            disabled={updatingScoringEnabled}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: 'success.main',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    bgcolor: 'success.main',
                                },
                            }}
                        />
                    </Box>
                </Box>

            </Box>
            <MatchDetailsDialog
                open={showMatch !== null}
                onClose={() => setShowMatch(null)}
                matchData={{ showMatch }}
            />

            <MatchLiveScoringDialog
                open={liveScoringDialogOpen}
                onClose={() => setLiveScoringDialogOpen(false)}
                showMatch={fixture}
            />

            <Dialog
                open={statusDialogOpen}
                onClose={() => setStatusDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Update Fixture Status</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel id="status-select-label">Status</InputLabel>
                        <Select
                            labelId="status-select-label"
                            id="status-select"
                            value={selectedStatus}
                            label="Status"
                            onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                            <MenuItem value="UPCOMING">UPCOMING</MenuItem>
                            <MenuItem value="LIVE">LIVE</MenuItem>
                            <MenuItem value="FINISHED">FINISHED</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleStatusChange} variant="contained">Update</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={scoringConfirmDialogOpen}
                onClose={() => setScoringConfirmDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm Scoring</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to run scoring for this fixture?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {fixture.details.HomeTeamName} vs {fixture.details.AwayTeamName}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setScoringConfirmDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleRunScoring} variant="contained" color="primary">
                        Run Scoring
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}
